import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Chip,
  Checkbox,
  LinearProgress,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Sports as SportsIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SelectAll as SelectAllIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { importService, SincSportsDivision, SincSportsDiscoveryResponse, SincSportsDiscoveryProgress } from '@/services/importService';

interface SincSportsDiscoveryDialogProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void; // For wizard mode - go back to previous step
  initialUrl?: string;
  autoStart?: boolean; // Automatically start discovery when opened
  onDivisionsSelected: (divisions: SincSportsDivision[], leagueName: string) => void;
}

const SincSportsDiscoveryDialog: React.FC<SincSportsDiscoveryDialogProps> = ({
  open,
  onClose,
  onBack,
  initialUrl = '',
  autoStart = false,
  onDivisionsSelected
}) => {
  // URL phase state
  const [url, setUrl] = useState(initialUrl);
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Progress state
  const [progress, setProgress] = useState<SincSportsDiscoveryProgress | null>(null);

  // Divisions phase state
  const [discoveryResult, setDiscoveryResult] = useState<SincSportsDiscoveryResponse | null>(null);
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [ageFilter, setAgeFilter] = useState<string | null>(null);
  const [genderFilter, setGenderFilter] = useState<string | null>(null);
  const [divisionFilter, setDivisionFilter] = useState<string | null>(null);

  // Teams preview state
  const [expandedDivisionId, setExpandedDivisionId] = useState<string | null>(null);
  const [divisionTeams, setDivisionTeams] = useState<Record<string, string[]>>({});
  const [loadingTeams, setLoadingTeams] = useState<string | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  // Track if we've auto-started
  const [hasAutoStarted, setHasAutoStarted] = useState(false);

  // Ref to track SSE connection for cleanup
  const sseRef = useRef<{ close: () => void } | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setDiscovering(false);
      setError(null);
      setProgress(null);
      setDiscoveryResult(null);
      setSelectedDivisionIds(new Set());
      setSearchQuery('');
      setAgeFilter(null);
      setGenderFilter(null);
      setDivisionFilter(null);
      setHasAutoStarted(false);
      // Reset teams preview state
      setExpandedDivisionId(null);
      setDivisionTeams({});
      setLoadingTeams(null);
      setTeamsError(null);
    } else {
      // Cleanup SSE connection when dialog closes
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    }
  }, [open, initialUrl]);

  // Auto-start discovery when dialog opens with autoStart=true
  useEffect(() => {
    if (open && autoStart && initialUrl && !hasAutoStarted && !discoveryResult) {
      setHasAutoStarted(true);
      handleDiscover();
    }
  }, [open, autoStart, initialUrl, hasAutoStarted, discoveryResult]);

  // Extract unique filter values from divisions
  const filterOptions = useMemo(() => {
    if (!discoveryResult?.divisions) return { ageGroups: [], genders: [], divisions: [] };

    const ageGroups = [...new Set(discoveryResult.divisions.map(d => d.ageGroup).filter(Boolean))] as string[];
    const genders = [...new Set(discoveryResult.divisions.map(d => d.gender).filter(Boolean))] as string[];
    const divisions = [...new Set(discoveryResult.divisions.map(d => d.division).filter(Boolean))] as string[];

    // Sort age groups numerically
    ageGroups.sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });

    // Sort divisions alphabetically
    divisions.sort((a, b) => a.localeCompare(b));

    return { ageGroups, genders, divisions };
  }, [discoveryResult?.divisions]);

  // Filter and search divisions
  const filteredDivisions = useMemo(() => {
    if (!discoveryResult?.divisions) return [];

    return discoveryResult.divisions.filter(division => {
      if (ageFilter && division.ageGroup !== ageFilter) return false;
      if (genderFilter && division.gender !== genderFilter) return false;
      if (divisionFilter && division.division !== divisionFilter) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          division.name.toLowerCase().includes(query) ||
          division.ageGroup?.toLowerCase().includes(query) ||
          division.gender?.toLowerCase().includes(query) ||
          division.division?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [discoveryResult?.divisions, ageFilter, genderFilter, divisionFilter, searchQuery]);

  // Handle URL discovery with SSE progress streaming
  const handleDiscover = async () => {
    const urlToUse = url || initialUrl;
    if (!urlToUse.trim()) {
      setError('Please enter a URL');
      return;
    }

    setDiscovering(true);
    setError(null);
    setProgress(null);

    // Close any existing SSE connection
    if (sseRef.current) {
      sseRef.current.close();
    }

    // Start SSE-based discovery with progress tracking
    const sse = await importService.discoverSincSportsDivisionsWithProgress(urlToUse, {
      onProgress: (progressUpdate) => {
        setProgress(progressUpdate);
      },
      onResult: (result) => {
        if (!result.success) {
          setError(result.error || 'Failed to discover divisions');
          setDiscovering(false);
          return;
        }

        if (result.divisions.length === 0) {
          setError('No divisions found at this URL. Make sure you entered a SincSports league page URL.');
          setDiscovering(false);
          return;
        }

        setDiscoveryResult(result);
        setDiscovering(false);
      },
      onError: (errorMessage) => {
        setError(errorMessage);
        setDiscovering(false);
      }
    });

    sseRef.current = sse;
  };

  // Handle division selection toggle
  const handleDivisionToggle = (divisionId: string) => {
    setSelectedDivisionIds(prev => {
      const next = new Set(prev);
      if (next.has(divisionId)) {
        next.delete(divisionId);
      } else {
        next.add(divisionId);
      }
      return next;
    });
  };

  // Handle select all visible divisions
  const handleSelectAll = () => {
    setSelectedDivisionIds(new Set(filteredDivisions.map(d => d.id)));
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedDivisionIds(new Set());
  };

  // Handle add selected divisions
  const handleAddSelected = () => {
    if (!discoveryResult) return;

    const selectedDivisions = discoveryResult.divisions.filter(d => selectedDivisionIds.has(d.id));
    onDivisionsSelected(selectedDivisions, discoveryResult.leagueName);
  };

  // Handle back button
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  // Handle viewing teams for a division
  const handleViewTeams = async (division: SincSportsDivision, event: React.MouseEvent) => {
    event.stopPropagation(); // Don't toggle checkbox when clicking view teams

    // If already expanded for this division, collapse it
    if (expandedDivisionId === division.id) {
      setExpandedDivisionId(null);
      return;
    }

    // Expand this division
    setExpandedDivisionId(division.id);
    setTeamsError(null);

    // If we already have teams for this division, don't fetch again
    if (divisionTeams[division.id]) {
      return;
    }

    // Fetch teams
    setLoadingTeams(division.id);
    try {
      const result = await importService.fetchSincSportsDivisionTeams(division.scheduleUrl);
      if (result.success) {
        setDivisionTeams(prev => ({ ...prev, [division.id]: result.teams }));
      } else {
        setTeamsError(result.error || 'Failed to load teams');
      }
    } catch (err) {
      setTeamsError('Failed to load teams');
    } finally {
      setLoadingTeams(null);
    }
  };

  // Clear filter
  const clearFilters = () => {
    setAgeFilter(null);
    setGenderFilter(null);
    setDivisionFilter(null);
    setSearchQuery('');
  };

  // Determine if we're in "discovering" or "selecting" mode
  const isDiscoveringPhase = !discoveryResult;
  const isSelectingPhase = !!discoveryResult;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableAutoFocus
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SportsIcon />
          <Typography variant="h6">
            {isSelectingPhase ? 'Select Divisions' : 'Discovering Divisions'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isDiscoveringPhase ? (
          // Discovery Phase
          <Box>
            {discovering ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <Box sx={{ width: '100%', maxWidth: 400, mb: 3 }}>
                  <LinearProgress
                    variant={progress?.percent !== undefined ? 'determinate' : 'indeterminate'}
                    value={progress?.percent || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  {progress?.percent !== undefined && (
                    <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                      {Math.round(progress.percent)}%
                    </Typography>
                  )}
                </Box>
                <Typography variant="h6" color="text.secondary">
                  {progress?.message || 'Discovering available divisions...'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {progress?.detail || 'This may take a moment'}
                </Typography>
                {progress?.divisionsFound !== undefined && progress.divisionsFound > 0 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Found {progress.divisionsFound} divisions{progress.divisionsProcessed !== undefined ? `, processing ${progress.divisionsProcessed}/${progress.divisionsFound}` : ''}
                  </Typography>
                )}
              </Box>
            ) : error ? (
              <Box>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <TextField
                  fullWidth
                  label="SincSports League URL"
                  placeholder="https://soccer.sincsports.com/schedule.aspx?div=N&tid=NCFL&year=2025"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  helperText="Check the URL and try again"
                  sx={{ mb: 2 }}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Ready to discover divisions
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Division Selection Phase
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Found {discoveryResult.divisions.length} divisions in "{discoveryResult.leagueName}"
            </Alert>

            {/* Search and Filters */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search divisions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </Button>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Filter chips */}
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                <FilterIcon fontSize="small" color="action" />

                {filterOptions.ageGroups.map(age => (
                  <Chip
                    key={age}
                    label={age}
                    size="small"
                    variant={ageFilter === age ? 'filled' : 'outlined'}
                    color={ageFilter === age ? 'primary' : 'default'}
                    onClick={() => setAgeFilter(ageFilter === age ? null : age)}
                  />
                ))}

                {filterOptions.ageGroups.length > 0 && filterOptions.genders.length > 0 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                )}

                {filterOptions.genders.map(gender => (
                  <Chip
                    key={gender}
                    label={gender}
                    size="small"
                    variant={genderFilter === gender ? 'filled' : 'outlined'}
                    color={genderFilter === gender ? 'secondary' : 'default'}
                    onClick={() => setGenderFilter(genderFilter === gender ? null : gender)}
                  />
                ))}

                {(filterOptions.genders.length > 0 || filterOptions.ageGroups.length > 0) && filterOptions.divisions.length > 0 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                )}

                {filterOptions.divisions.map(division => (
                  <Chip
                    key={division}
                    label={division}
                    size="small"
                    variant={divisionFilter === division ? 'filled' : 'outlined'}
                    color={divisionFilter === division ? 'info' : 'default'}
                    onClick={() => setDivisionFilter(divisionFilter === division ? null : division)}
                  />
                ))}

                {(ageFilter || genderFilter || divisionFilter || searchQuery) && (
                  <Button size="small" onClick={clearFilters} sx={{ ml: 1 }} startIcon={<ClearIcon />}>
                    Clear filters
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Selection controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredDivisions.length} divisions shown, {selectedDivisionIds.size} selected
              </Typography>
              <Box>
                <Button size="small" onClick={handleSelectAll} disabled={filteredDivisions.length === 0} startIcon={<SelectAllIcon />}>
                  Select All
                </Button>
                <Button size="small" onClick={handleClearSelection} disabled={selectedDivisionIds.size === 0} startIcon={<ClearIcon />}>
                  Clear
                </Button>
              </Box>
            </Box>

            {/* Divisions list */}
            <List sx={{ maxHeight: '350px', overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
              {filteredDivisions.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No divisions match your filters"
                    secondary="Try adjusting your search or filters"
                  />
                </ListItem>
              ) : (
                filteredDivisions.map(division => (
                  <Box key={division.id}>
                    <ListItem
                      disablePadding
                      secondaryAction={
                        <Button
                          size="small"
                          onClick={(e) => handleViewTeams(division, e)}
                          endIcon={
                            expandedDivisionId === division.id ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )
                          }
                          sx={{
                            mr: 0.5,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto'
                          }}
                        >
                          {expandedDivisionId === division.id ? 'Hide teams' : 'Show teams'}
                        </Button>
                      }
                    >
                      <ListItemButton onClick={() => handleDivisionToggle(division.id)} dense>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedDivisionIds.has(division.id)}
                            tabIndex={-1}
                            disableRipple
                            icon={<CheckBoxOutlineBlankIcon />}
                            checkedIcon={<CheckBoxIcon />}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={division.name}
                          secondary={
                            <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {division.ageGroup && (
                                <Chip label={division.ageGroup} size="small" variant="outlined" />
                              )}
                              {division.gender && (
                                <Chip label={division.gender} size="small" variant="outlined" />
                              )}
                              {division.division && (
                                <Chip label={division.division} size="small" variant="outlined" />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>

                    {/* Collapsible teams section */}
                    <Collapse in={expandedDivisionId === division.id} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 7, pr: 2, pb: 1.5, pt: 0.5, bgcolor: 'grey.100' }}>
                        {loadingTeams === division.id ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                              Loading teams...
                            </Typography>
                          </Box>
                        ) : teamsError && expandedDivisionId === division.id ? (
                          <Typography variant="body2" color="error">
                            {teamsError}
                          </Typography>
                        ) : divisionTeams[division.id]?.length > 0 ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                              {divisionTeams[division.id].length} teams in this division:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {divisionTeams[division.id].map((team, idx) => (
                                <Chip
                                  key={idx}
                                  label={team}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ) : divisionTeams[division.id]?.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No teams found in this division
                          </Typography>
                        ) : null}
                      </Box>
                    </Collapse>
                  </Box>
                ))
              )}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleBack}
          disabled={discovering}
          startIcon={onBack ? <ArrowBackIcon /> : <CancelIcon />}
        >
          {onBack ? 'Back' : 'Cancel'}
        </Button>

        {isDiscoveringPhase && error && (
          <Button
            variant="contained"
            onClick={handleDiscover}
            disabled={discovering || !url.trim()}
            startIcon={<RefreshIcon />}
          >
            Retry
          </Button>
        )}

        {isSelectingPhase && (
          <Button
            variant="contained"
            onClick={handleAddSelected}
            disabled={selectedDivisionIds.size === 0}
            startIcon={<AddIcon />}
          >
            Add ({selectedDivisionIds.size})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SincSportsDiscoveryDialog;
